import * as yup from "yup";
import toast from "react-hot-toast";
import CloseButton from "../CloseButton";
import TextInput from "../Inputs/TextInput";
import { Modal } from "../Modal";
import TextArea from "../TextArea/TextArea";
import AppLoader from "../ui/AppLoader";
import Button from "../ui/Button";
import ButtonsContainer from "../ui/ButtonsContainer";
import { AxiosError } from "axios";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useForm } from "react-hook-form";
import { useReportError } from "../../hooks/useReportError";
import resources from "../../resources.json";
import { closeModel as closeReportErrorModal } from "../../reducers/error.slice";

const ReportErrorSchema = yup.object().shape({
  name: yup
    .string()
    .required(resources.errorMessage.REPORT_ERROR_NAME_REQUIRED_ERROR)
    .default(""),
  description: yup.string(),
});

export function ErrorReportingModal() {
  const dispatch = useDispatch();
  const { refId, is500Error } = useSelector(
    (state: RootState) => state.reportError,
  );

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    reset,
  } = useForm({
    defaultValues: { name: "", description: "" },
    resolver: yupResolver(ReportErrorSchema),
  });

  const { isLoading, mutate: reportError } = useReportError();

  const closeModal = () => {
    dispatch(closeReportErrorModal());
    reset();
  };

  const onSubmit = handleSubmit((formValues) => {
    closeModal();
    const formData = new FormData();
    Object.entries(formValues).forEach(([key, value]) =>
      formData.append(key, value),
    );
    formData.append("refId", refId);

    reportError(formData, {
      onSuccess: (response) => {
        if (response.status === 200) {
          toast.success(resources.successMessage.ERROR_REPORT_SUCCESS_MESSAGE);
        }
      },
      onError: (err) => {
        if (err instanceof AxiosError && err.response?.status === 400) {
          err.response.data.apiexceptionresponse.subErrors?.forEach(
            (error: { field: any; message: string }) => {
              setError(error.field, { message: error.message });
            },
          );
        }
      },
    });
  });

  return (
    <>
      {isLoading && <AppLoader />}
      <Modal
        open={is500Error}
        titleContent={
          <p>{resources.modal.heading.SOMETHING_WENT_WRONG_HEADING}</p>
        }
        content={
          <form className="form_gap" onSubmit={onSubmit}>
            <TextInput
              label={resources.label.NAME_LABEL}
              register={register("name")}
              required
              error={errors.name?.message}
            />
            <TextArea
              label={resources.label.DESCRIPTION_LABEL}
              rows={5}
              register={register("description")}
              maxLength={150}
              error={errors.description?.message}
            />
          </form>
        }
        actions={
          <ButtonsContainer>
            <Button buttonType="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button buttonType="primary" onClick={onSubmit}>
              Report
            </Button>
          </ButtonsContainer>
        }
        closeFn={closeModal}
        escFn={closeModal}
        closeIcon={<CloseButton onClose={closeModal} />}
      />
    </>
  );
}
